import { getSiteSettings } from "@config/site/site-api-settings";
import { getPageContentBySlug } from "@contentful-api/queries/pagePath/pagePathService";
import { getSiteSettingsContent } from "@contentful-api/queries/siteSettings/siteSettingsService";
import type { NextPageWithLayout } from "@interfaces/PageInterfaces";
import { customerServicePages, getPagePathByLocale } from "@lib/constants/pagePaths";
import { COMMON_NEXT_CACHE_TTL } from "@lib/constants/revalidation";
import { PageTypes } from "@lib/enums/PageTypes";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { getCompleteSlug } from "@lib/utils/getCompleteSlug";
import { getCustomerServiceLayout } from "@lib/utils/layoutUtility";
import { serialize } from "@ungap/structured-clone";
import type { GetStaticPaths, GetStaticPropsContext, PreviewData } from "next";
import type { ParsedUrlQuery } from "querystring";
import { TwoColumnsPage } from "templates/TwoColumnPage";

type CustomerServiceProps = Awaited<ReturnType<typeof getStaticProps>>["props"];

const CustomerServicePage: NextPageWithLayout<CustomerServiceProps> = (props) => {
    return (
        <>
            <div hidden aria-hidden="true" data-testid="customerServiceSlug" />
            <TwoColumnsPage {...props} />
        </>
    );
};

export default CustomerServicePage;

CustomerServicePage.getLayout = getCustomerServiceLayout;

export const getStaticProps = async ({
    locale,
    params,
}: GetStaticPropsContext<ParsedUrlQuery, PreviewData>) => {
    const startTime = performance.now();

    const [siteSettings, pagePath] = await Promise.all([
        getSiteSettingsContent({
            locale,
            localeTagId: getSiteSettings.contentfulLocaleTagId,
            pageType: PageTypes.CUSTOMER_SERVICE,
            selectedFields: ["fields.sizeGuideModal"],
            microcopySetKey: "checkout",
        }),
        getPageContentBySlug({
            locale,
            localeTagId: getSiteSettings.contentfulLocaleTagId,
            pageType: PageTypes.CUSTOMER_SERVICE,
            basePath: customerServicePages,
            slug: params?.slug,
        }),
    ]);

    if (!pagePath) {
        return {
            notFound: true,
            revalidate: COMMON_NEXT_CACHE_TTL,
        };
    }

    const { content, sharedMicrocopySet } = siteSettings ?? {};

    // Serialize Contentful data to remove circular references and ensure it's JSON-safe for Next.js
    const serializedSiteSettings = serialize(content);
    const serializedSharedMicrocopySet = serialize(sharedMicrocopySet);
    const serializedSizeGuideModal = serialize(content.fields.sizeGuideModal);
    const serializedPage = serialize(pagePath.fields?.page);
    const pathName = getCompleteSlug(
        params?.slug,
        getPagePathByLocale(customerServicePages, locale)
    );

    Logger.info(ServiceType.NEXT_API, "CustomerServicePage Total Time", {
        tag: LogTag.PERFORMANCE,
        duration: performance.now() - startTime,
        pageType: PageTypes.CUSTOMER_SERVICE,
    });

    return {
        props: {
            serializedSiteSettings,
            serializedSharedMicrocopySet,
            serializedSizeGuideModal,
            serializedPage,
            pathName,
            pageType: PageTypes.CUSTOMER_SERVICE,
        },
        revalidate: 300,
    };
};

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        paths: [],
        fallback: "blocking",
    };
};
